import { Alert, Stack, TextField, Typography } from '@mui/material';
import MDButton from 'components/MDButton';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useStore } from 'store';
import { EAuthStage } from 'store/AuthStore';
import { requestResetPassword } from 'auth/api';
import { authError } from 'auth/errors';

function Login() {
  const { AuthStore: auth } = useStore();
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [notice, setNotice] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetAfter, setResetAfter] = useState(0);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    auth.initialize();
  }, [auth]);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (auth.authStage === EAuthStage.AUTHENTICATED) router.replace('/dashboard');
  }, [auth.authStage, router]);
  useEffect(() => {
    if (
      auth.challengeExpiresAt &&
      now >= auth.challengeExpiresAt &&
      [EAuthStage.PASSWORD_CHALLENGE, EAuthStage.TWO_FA_CHALLENGE].includes(auth.authStage)
    ) {
      auth.startOver();
      setNotice('Your sign-in challenge expired. Please start again.');
    }
  }, [now, auth.authStage, auth.challengeExpiresAt, auth]);
  useEffect(() => {
    setPassword('');
    setCode('');
    setNow(Date.now());
  }, [auth.authStage]);

  const resetPassword = async () => {
    if (resetting || Date.now() < resetAfter) return;
    setResetting(true);
    setNotice('');
    setResetError('');
    try {
      await requestResetPassword(auth.signinEmail || email);
      setNotice('If eligible, an email will be sent.');
      setResetAfter(Date.now() + 60000);
    } catch (error) {
      const failure = authError(error);
      setResetError(failure.message);
      if (failure.status === 429) setResetAfter(Date.now() + 60000);
    } finally {
      setResetting(false);
    }
  };

  const stage = auth.authStage;
  if (stage === EAuthStage.SERVICE_ERROR)
    return (
      <Stack spacing={2} p={3}>
        <Alert severity="error">{auth.errors.initialize}</Alert>
        <MDButton color="primary" variant="text" onClick={() => auth.initialize(true)}>
          Retry
        </MDButton>
      </Stack>
    );
  if ([EAuthStage.IDLE, EAuthStage.INITIALIZING, EAuthStage.AUTHENTICATED].includes(stage))
    return <Typography p={3}>Restoring your session…</Typography>;
  const error = auth.errors.checkEmail || auth.errors.login || auth.errors.verify || resetError;
  return (
    <Stack spacing={3} p={3}>
      <Typography variant="h4">Sign in to PBB</Typography>
      {notice && <Alert severity="info">{notice}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      {now < auth.retryAfter && (
        <Alert severity="info">
          Try again in {Math.ceil((auth.retryAfter - now) / 1000)} seconds.
        </Alert>
      )}
      {stage === EAuthStage.CHECK_EMAIL && (
        <Stack
          component="form"
          spacing={2}
          onSubmit={(event) => {
            event.preventDefault();
            setNotice('');
            auth.checkEmail(email);
          }}
        >
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoComplete="username"
            inputProps={{ maxLength: 254 }}
          />
          <MDButton
            color="primary"
            type="submit"
            variant="contained"
            disabled={auth.isLoading.checkEmail || now < auth.retryAfter}
          >
            {auth.isLoading.checkEmail ? 'Continuing…' : 'Next'}
          </MDButton>
        </Stack>
      )}
      {stage === EAuthStage.PASSWORD_CHALLENGE && (
        <Stack
          component="form"
          spacing={2}
          onSubmit={(event) => {
            event.preventDefault();
            const submitted = password;
            setPassword('');
            auth.login(submitted);
          }}
        >
          <Typography>{auth.signinEmail}</Typography>
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            autoComplete="current-password"
          />
          <MDButton
            color="primary"
            type="submit"
            variant="contained"
            disabled={auth.isLoading.login || now < auth.retryAfter}
          >
            {auth.isLoading.login ? 'Signing in…' : 'Sign in'}
          </MDButton>
        </Stack>
      )}
      {stage === EAuthStage.TWO_FA_CHALLENGE && (
        <Stack
          component="form"
          spacing={2}
          onSubmit={(event) => {
            event.preventDefault();
            const submitted = code;
            setCode('');
            auth.verify({ code: submitted });
          }}
        >
          <Typography>
            {auth.mfaMethod === 'totp'
              ? 'Enter the code from your authenticator.'
              : 'Enter the code sent to your email.'}
          </Typography>
          <TextField
            label="Verification code"
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            autoComplete="one-time-code"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]{6}', maxLength: 6 }}
            required
          />
          <MDButton
            color="primary"
            type="submit"
            variant="contained"
            disabled={code.length !== 6 || auth.isLoading.verify || now < auth.retryAfter}
          >
            {auth.isLoading.verify ? 'Verifying…' : 'Continue'}
          </MDButton>
          {auth.mfaMethod === 'email' && (
            <MDButton
              color="primary"
              variant="text"
              onClick={() => auth.resend()}
              disabled={now < auth.resendAt || auth.isLoading.resend || now < auth.retryAfter}
            >
              {now < auth.resendAt
                ? `Resend in ${Math.ceil((auth.resendAt - now) / 1000)}s`
                : 'Resend code'}
            </MDButton>
          )}
          <Typography variant="caption">
            This challenge expires in{' '}
            {Math.max(0, Math.ceil((auth.challengeExpiresAt - now) / 1000))} seconds.
          </Typography>
        </Stack>
      )}
      {[EAuthStage.CHECK_EMAIL, EAuthStage.PASSWORD_CHALLENGE].includes(stage) && (
        <MDButton
          color="primary"
          variant="text"
          onClick={resetPassword}
          disabled={
            resetting ||
            now < resetAfter ||
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(auth.signinEmail || email)
          }
        >
          {resetting ? 'Requesting…' : 'Forgot password?'}
        </MDButton>
      )}
      {stage !== EAuthStage.CHECK_EMAIL && (
        <MDButton
          color="primary"
          variant="text"
          onClick={() => {
            auth.startOver();
            setNotice('');
          }}
        >
          Start again with email
        </MDButton>
      )}
    </Stack>
  );
}
export default observer(Login);
